import FolderOutlined from "@mui/icons-material/FolderOutlined";
import LocalOfferOutlined from "@mui/icons-material/LocalOfferOutlined";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { AssetPreview } from "../message/editor/plugins/toolbar/content-library/asset-preview";
import { useDragState } from "./hooks/use-drag-state";

import type { Asset } from "./server/digital-asset-service";
import type { CardProps } from "@mui/material";
import type { DragEvent, FC } from "react";

export const AssetCard: FC<
  {
    asset: Asset;
    folderName: string | null;
    selected?: boolean;
    selectionMode?: boolean;
    disableZoomIcon?: boolean;
    designer?: string | null;
    onPreview?: () => void;
  } & CardProps
> = ({
  asset,
  folderName,
  selected,
  selectionMode = false,
  disableZoomIcon = false,
  designer,
  onDragStart,
  onDragEnd,
  onPreview,
  ...props
}) => {
  const [hover, setHovering] = useState<boolean>(false);

  const { isDragging, handlers } = useDragState<HTMLDivElement>({
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    onDragStart: onDragStart as (event: DragEvent<HTMLDivElement>) => void,
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    onDragEnd: onDragEnd as (event: DragEvent<HTMLDivElement>) => void,
  });

  return (
    <Box display="flex" flexDirection="column" width="100%">
      <Card
        variant="outlined"
        {...props}
        onDragStart={handlers.handleDragStart}
        onDragEnd={handlers.handleDragEnd}
        sx={{
          cursor: props.draggable ? (isDragging ? "grabbing" : "grab") : undefined,
          opacity: isDragging ? 0.5 : 1,
          ...(selected
            ? {
                borderColor: "primary.main",
                boxShadow:
                  "inset 0 0 0 3px var(--mui-palette-primary-main), 0 0 0 2px rgba(25, 118, 210, 0.25)",
                backgroundImage:
                  "linear-gradient(rgba(25, 118, 210, 0.05), rgba(25, 118, 210, 0.05))",
              }
            : {}),
          ...props.sx,
        }}
      >
        <CardActionArea
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
          sx={{
            cursor: props.draggable ? "inherit" : undefined,
          }}
        >
          <Box sx={{ position: "relative" }}>
            {selectionMode && (
              <Checkbox
                checked={Boolean(selected)}
                sx={{
                  position: "absolute",
                  top: 8,
                  left: 8,
                  zIndex: 1,
                  bgcolor: "rgba(255, 255, 255, 0.95)",
                  "&:hover": {
                    bgcolor: "rgba(255, 255, 255, 0.62)",
                  },
                  borderRadius: 1,
                }}
                slotProps={{ input: { "aria-label": `Select ${asset.title}` } }}
              />
            )}
            <AssetPreview
              asset={asset}
              height="200px"
              iconFontSize={120}
              showPreviewIcon={Boolean(hover && !disableZoomIcon && !selectionMode)}
              videoPlayer={false}
              designer={designer}
              onPreviewClick={onPreview}
            />
          </Box>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="subtitle1" noWrap textOverflow="ellipsis">
                {asset.title}
              </Typography>
              {asset.content_type && (
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Chip
                    label={asset.content_type}
                    data-testid={`${asset.content_type}-${asset.title}`}
                  />
                  {asset.tags.length > 0 && (
                    <Tooltip
                      title={
                        <Box>
                          {asset.tags.map((tag, index) => (
                            <span key={index}>
                              {tag}
                              <br />
                            </span>
                          ))}
                        </Box>
                      }
                      arrow
                    >
                      <LocalOfferOutlined sx={{ fontSize: "16px", color: "text.secondary" }} />
                    </Tooltip>
                  )}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
      {folderName ? (
        <Stack direction="row" alignItems="center" spacing={0.5} mt={1} minWidth={0}>
          <FolderOutlined fontSize="small" />
          <Typography variant="body2" noWrap>
            {folderName}
          </Typography>
        </Stack>
      ) : null}
    </Box>
  );
};
